import type { ApiContext, ApiNotAuthorized, InvalidFormField, UploadedImage } from "@hobbyshopify/backend-client";
import { type AsyncResult, fail } from "operation-result.js";
import { BaseController, Rx, type SubtreeModel } from "subtree.js/core";
import { AppError, type AuthHandler } from "trunk.js";
import type { AppDeps } from "../../app-deps";
import type { BackendApi } from "../../core/ops/api";
import { readImageFile } from "../../core/primitives/image-upload";

/** The file could not be read, or is over the size limit; `message` is the sentence to show. */
export class ImageUnreadable extends AppError {}

/**
 * Hosts a picked logo. The API is JSON end to end, so the image travels as a
 * `data:` URL, the same shape the scan pipeline accepts.
 */
export async function uploadLogo(o: {
  client: BackendApi;
  authHandler: AuthHandler<ApiContext>;
  file: File;
}): AsyncResult<UploadedImage, ApiNotAuthorized | InvalidFormField | ImageUnreadable> {
  let image: string;
  try {
    image = await readImageFile(o.file);
  } catch (e) {
    return fail(new ImageUnreadable(e instanceof Error ? e.message : "Could not read that file.", e));
  }
  return o.authHandler.callApi((ctx) => o.client.uploads.logo(ctx, image));
}

export class LogoFieldState {
  /** The hosted URL the form submits; "" when there is no logo. */
  readonly url = new Rx("");
  readonly uploading = new Rx(false);
  readonly error = new Rx<string | null>(null);
}

export abstract class LogoFieldActions {
  /** Uploads on selection rather than on submit, so the preview is proof the image is really hosted. */
  abstract pick(file: File): Promise<void>;
  abstract clear(): void;
}

export type LogoFieldDeps = Pick<AppDeps, "client" | "authHandler">;

/**
 * The logo control of a shop form: a section the page owns, seeded from the
 * shop and read back on save. The value stays a URL, as the shop record has
 * always stored one; only where it comes from changed.
 */
export class LogoFieldController extends BaseController implements LogoFieldActions {
  readonly state = new LogoFieldState();

  constructor(
    subtree: SubtreeModel,
    readonly deps: LogoFieldDeps,
  ) {
    super();
    subtree.put(LogoFieldState, this.state);
    subtree.put(LogoFieldActions, this);
  }

  /** What the shop has now; called when the form is (re)seeded. */
  seed(url: string | null | undefined): void {
    this.state.url.value = url ?? "";
    this.state.error.value = null;
  }

  pick = async (file: File) => {
    if (this.state.uploading.value) return;
    this.state.uploading.value = true;
    this.state.error.value = null;
    try {
      const r = await uploadLogo({ ...this.deps, file });
      if (this.isDisposed) return;
      if (!r.ok) {
        this.state.error.value = r.errors[0].message;
        return;
      }
      this.state.url.value = r.value.url;
    } finally {
      this.state.uploading.value = false;
    }
  };

  clear = () => {
    this.state.error.value = null;
    this.state.url.value = "";
  };
}
